import React, { useState, useEffect, useCallback } from 'react';
import { X, BarChart3, FileText, Download, Calendar, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';
import { api, apiBlob } from '../../utils/api';
import { toast } from '../ui/simple-toaster';

/**
 * ReportsModal - Reports and Compliance view for owner/drivers
 * Shows compliance status, violations, and export options
 */
const ReportsModal = ({ 
  isOpen, 
  onClose, 
  driverId,
  driverName
}) => {
  const [loading, setLoading] = useState(true);
  const [compliance, setCompliance] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedRange, setSelectedRange] = useState('7'); // days
  
  const loadCompliance = useCallback(async () => {
    setLoading(true);
    try {
      // Use the correct driver state endpoint
      const data = await api(`/drivers/${driverId}/state`);
      setCompliance(data);
    } catch (err) {
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, [driverId]);

  useEffect(() => {
    if (isOpen && driverId) {
      loadCompliance();
    }
  }, [isOpen, driverId, loadCompliance]);
  
  const handleExportPDF = async () => {
    setExportLoading(true);
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - parseInt(selectedRange) * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const blob = await apiBlob(`/reports/driver/${driverId}/pdf?start_date=${startDate}&end_date=${endDate}`);
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `work_diary_${driverName?.replace(/\s+/g, '_') || 'driver'}_${startDate}_to_${endDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error('Failed to export PDF: ' + err.message);
    } finally {
      setExportLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  // Determine if there are any compliance issues
  const hasViolations = compliance?.compliance_status === 'BEYOND_LAW' || 
                        compliance?.compliance_status === 'LIMIT_REACHED' ||
                        compliance?.violations?.length > 0;
  
  const complianceColor = {
    'GREEN': 'emerald',
    'SHADOW_LAW': 'amber',
    'WARNING': 'amber', 
    'BEYOND_LAW': 'red',
    'LIMIT_REACHED': 'red'
  }[compliance?.compliance_status] || 'slate';
  
  const complianceLabel = {
    'GREEN': 'Fully Compliant',
    'SHADOW_LAW': 'In Shadow Zone',
    'WARNING': 'Warning',
    'BEYOND_LAW': 'Beyond Law',
    'LIMIT_REACHED': 'Limit Reached'
  }[compliance?.compliance_status] || 'Unknown';
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-2xl w-full max-w-lg border border-slate-700 shadow-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Reports & Compliance</h3>
              <p className="text-sm text-slate-400">Your compliance status</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg" data-testid="reports-close">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-slate-600 border-t-purple-500 rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Compliance Status */}
              <div className={`p-4 rounded-xl border ${
                hasViolations 
                  ? 'bg-red-500/10 border-red-500/30' 
                  : `bg-${complianceColor}-500/10 border-${complianceColor}-500/30`
              }`}>
                <div className="flex items-center gap-3">
                  {hasViolations ? (
                    <AlertTriangle className="w-8 h-8 text-red-500" />
                  ) : (
                    <CheckCircle className={`w-8 h-8 text-${complianceColor}-500`} />
                  )}
                  <div>
                    <h4 className={`font-bold ${hasViolations ? 'text-red-400' : `text-${complianceColor}-400`}`}>
                      {complianceLabel}
                    </h4>
                    <p className="text-sm text-slate-400">
                      {compliance?.regime_label || 'Loading...'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-700/50 rounded-xl">
                  <span className="text-xs text-slate-400 block">Work (24h)</span>
                  <span className="text-xl font-bold text-white">
                    {compliance?.work_24h_seconds ? `${(compliance.work_24h_seconds / 3600).toFixed(1)}h` : '—'}
                  </span>
                </div>
                <div className="p-3 bg-slate-700/50 rounded-xl">
                  <span className="text-xs text-slate-400 block">Continuous Work</span>
                  <span className="text-xl font-bold text-white">
                    {compliance?.continuous_work_seconds !== undefined 
                      ? `${(compliance.continuous_work_seconds / 3600).toFixed(1)}h` 
                      : '—'}
                  </span>
                </div>
                <div className="p-3 bg-slate-700/50 rounded-xl">
                  <span className="text-xs text-slate-400 block">Rest Credits</span>
                  <span className="text-xl font-bold text-emerald-400">
                    {compliance?.R_credits_24 ?? '—'} / {compliance?.max_credits_day ?? 4}
                  </span>
                </div>
                <div className="p-3 bg-slate-700/50 rounded-xl">
                  <span className="text-xs text-slate-400 block">Regime</span>
                  <span className="text-xl font-bold text-blue-400">
                    {compliance?.regime || 'FRHV'}
                  </span>
                </div>
              </div>
              
              {/* Violations List */}
              {hasViolations && (
                <div className="space-y-2">
                  <h4 className="font-medium text-white flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    Active Violations
                  </h4>
                  {compliance.violations.map((v) => (
                    <div key={v.rule || v.type || v.description} className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-sm">
                      <div className="font-medium text-red-400">{v.rule || v.type}</div>
                      <div className="text-slate-400 mt-1">{v.description || v.message}</div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Export Section */}
              <div className="border-t border-slate-700 pt-4 mt-4">
                <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Export Work Diary
                </h4>
                
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-sm text-slate-400">Period:</span>
                  <select
                    value={selectedRange}
                    onChange={(e) => setSelectedRange(e.target.value)}
                    className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                    data-testid="export-range"
                  >
                    <option value="7">Last 7 days</option>
                    <option value="14">Last 14 days</option>
                    <option value="28">Last 28 days</option>
                    <option value="90">Last 90 days</option>
                  </select>
                </div>
                
                <button
                  onClick={handleExportPDF}
                  disabled={exportLoading}
                  className="w-full py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-xl font-medium transition disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="export-pdf-btn"
                >
                  {exportLoading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Download className="w-5 h-5" />
                      Download PDF
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportsModal;
